/**
 * FinanceDashboardPage — Hospital 360° Finance view.
 *
 * Route: /finance-dashboard  (perm: dashboard.finance)
 */
import { useEffect, useMemo, useState } from 'react';
import { finance360API } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, Area, AreaChart,
  BarChart, Bar,
} from 'recharts';
import {
  IndianRupee, Users, TrendingUp, Building2, Download, Calendar, ArrowRight,
  Wallet, Activity, BadgePercent, UserCheck, X, Printer, CreditCard,
} from 'lucide-react';

const PIE_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#ef4444', '#14b8a6', '#f97316', '#6366f1'];
const fmtINR = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const FinanceDashboardPage = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState('month');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [deptView, setDeptView] = useState('hospital');  // hospital | specialization
  const [department, setDepartment] = useState('all');
  const [staffId, setStaffId] = useState('all');
  const [granularity, setGranularity] = useState('day');
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [filterOpts, setFilterOpts] = useState({ departments: [], staff: [] });
  // v38.4 — When user clicks a slice in the "Revenue by Payment Method" pie, this
  // filters the Invoice Details table on the same page. Other rows are greyed out.
  const [paymentMethodFilter, setPaymentMethodFilter] = useState(null);

  const canView = hasPermission ? hasPermission('dashboard.finance') : false;

  useEffect(() => {
    if (!canView) return;
    finance360API.filterOptions()
      .then(r => setFilterOpts(r.data))
      .catch(() => {});
  }, [canView]);

  useEffect(() => { if (canView) load(); setPaymentMethodFilter(null); /* eslint-disable-next-line */ }, [canView, range, start, end, deptView, department, staffId]);
  useEffect(() => { if (canView) loadTrend(); /* eslint-disable-next-line */ }, [canView, range, start, end, granularity, department, staffId]);

  const buildParams = () => {
    const p = { range, department_view: deptView };
    if (range === 'custom') { p.start = start; p.end = end; }
    if (staffId && staffId !== 'all') p.staff_id = staffId;
    else if (department && department !== 'all') p.department = department;
    return p;
  };

  // When a staff is picked, filter the visible staff list by their department; clear staff when department changes.
  const filteredStaff = useMemo(() => {
    if (department === 'all') return filterOpts.staff;
    return filterOpts.staff.filter(s => (s.department || '').trim() === department);
  }, [department, filterOpts.staff]);

  const load = async () => {
    if (range === 'custom' && (!start || !end)) return;
    setLoading(true);
    try {
      const res = await finance360API.dashboard(buildParams());
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load dashboard'); }
    finally { setLoading(false); }
  };

  const loadTrend = async () => {
    if (range === 'custom' && (!start || !end)) return;
    try {
      const res = await finance360API.trend({ ...buildParams(), granularity });
      setTrend(res.data);
    } catch (e) { /* silent */ }
  };

  const drill = async (side, bucket) => {
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillData(null);
    try {
      const res = await finance360API.drilldown({ ...buildParams(), side, bucket });
      setDrillData(res.data);
    } catch (e) { toast.error('Drill-down failed'); }
    finally { setDrillLoading(false); }
  };

  const exportCsv = async (side) => {
    try {
      const res = await finance360API.exportCsv({ ...buildParams(), side });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `finance_${side}_${range}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${side === 'revenue' ? 'Revenue' : 'Expense'} CSV downloaded`);
    } catch (e) { toast.error('Export failed'); }
  };

  const seeAllForBucket = (side, bucket) => {
    setDrillOpen(false);
    if (side === 'revenue') navigate(`/invoice-history?dept=${encodeURIComponent(bucket)}`);
    else navigate(`/expenses?category=${encodeURIComponent(bucket)}`);
  };

  if (!canView) {
    return <div className="p-8 text-slate-500" data-testid="finance-dash-restricted">You don't have permission to view the Finance Dashboard.</div>;
  }

  return (
    <div className="space-y-5" data-testid="finance-dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-md">
            <Wallet size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Finance Dashboard <span className="text-base font-normal text-slate-500">· Hospital 360°</span></h1>
            <p className="text-sm text-slate-500">Where every rupee comes from and where every rupee goes.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => exportCsv('revenue')} data-testid="csv-revenue"><Download size={14} className="mr-1.5" /> Revenue CSV</Button>
          <Button variant="outline" onClick={() => exportCsv('expense')} data-testid="csv-expense"><Download size={14} className="mr-1.5" /> Expense CSV</Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[160px]">
            <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Range</p>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger data-testid="fin-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="week">Last 7 days</SelectItem>
                <SelectItem value="month">Last 30 days</SelectItem>
                <SelectItem value="year">Year-to-date</SelectItem>
                <SelectItem value="custom">Custom…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {range === 'custom' && (
            <>
              <div><p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Start</p><Input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="fin-start" /></div>
              <div><p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">End</p><Input type="date" value={end} onChange={e => setEnd(e.target.value)} data-testid="fin-end" /></div>
            </>
          )}
          <div className="min-w-[200px]">
            <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Department lens</p>
            <Select value={deptView} onValueChange={setDeptView}>
              <SelectTrigger data-testid="fin-deptview"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="hospital">By Hospital Dept (OPD/IPD/Lab/Pharmacy…)</SelectItem>
                <SelectItem value="specialization">By Doctor Specialization</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Department filter</p>
            <Select value={department} onValueChange={(v) => { setDepartment(v); setStaffId('all'); }}>
              <SelectTrigger data-testid="fin-dept-filter"><SelectValue placeholder="All departments" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {filterOpts.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px]">
            <p className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Staff filter</p>
            <Select value={staffId} onValueChange={setStaffId}>
              <SelectTrigger data-testid="fin-staff-filter"><SelectValue placeholder="All staff" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">All staff{department !== 'all' ? ` in ${department}` : ''}</SelectItem>
                {filteredStaff.map(s => (
                  <SelectItem key={s.user_id} value={s.user_id}>
                    {s.name}{s.employee_code ? ` · ${s.employee_code}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(department !== 'all' || staffId !== 'all') && (
            <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={() => { setDepartment('all'); setStaffId('all'); }} data-testid="fin-clear-filters">
              <X size={12} className="mr-1" /> Clear filters
            </Button>
          )}
          {data && <div className="text-xs text-slate-500 ml-auto">Showing: <strong>{data.range.label}</strong> · {data.range.days} day{data.range.days > 1 ? 's' : ''}{data.filters?.staff_id ? ` · staff filter active` : data.filters?.department ? ` · ${data.filters.department}` : ''}</div>}
        </CardContent>
      </Card>

      {loading || !data ? (
        <div className="p-12 text-center text-slate-400 text-sm">Crunching the numbers…</div>
      ) : (
        <>
          {/* v38.3 — Diagnostic alerts (e.g., filtered staff have no salary configured) */}
          {data.alerts?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="finance-alerts">
              <Activity className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                {data.alerts.map((msg, i) => (
                  <p key={i} className="text-xs text-amber-800">{msg}</p>
                ))}
                <button onClick={() => navigate('/staff')} className="text-[11px] font-semibold text-amber-700 underline mt-1" data-testid="finance-alert-fix-link">
                  Open Staff Management →
                </button>
              </div>
            </div>
          )}

          {/* KPIs — Revenue Side */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi color="emerald" icon={IndianRupee} label="Total Revenue" value={fmtINR(data.kpis.total_revenue)} onClick={() => drill('revenue', data.revenue.by_department[0]?.name || 'OPD')} testid="kpi-total-revenue" />
            <Kpi color="blue" icon={Users} label="Total Patients" sub={`${data.kpis.appointments_in_range} visits`} value={data.kpis.total_patients} onClick={() => navigate('/patients')} testid="kpi-patients" />
            <Kpi color="amber" icon={Activity} label="Avg / Patient" value={fmtINR(data.kpis.avg_revenue_per_patient)} testid="kpi-avg-patient" />
            <Kpi color="violet" icon={Building2} label="Avg / Dept" value={fmtINR(data.kpis.avg_revenue_per_department)} testid="kpi-avg-dept" />
          </div>

          {/* KPIs — Expense Side */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi color="rose" icon={TrendingUp} label="Total Expense" value={fmtINR(data.kpis.total_expense)} onClick={() => drill('expense', data.expense.by_category[0]?.name || 'Salaries')} testid="kpi-total-expense" />
            <Kpi color="orange" icon={UserCheck} label="Total Staff" sub={`${data.expense.staff_by_department.length} departments`} value={data.kpis.total_staff} onClick={() => navigate('/staff')} testid="kpi-staff" />
            <Kpi color="rose" icon={IndianRupee} label="Expense / Staff" value={fmtINR(data.kpis.expense_per_staff)} testid="kpi-exp-staff" />
            <Kpi color={data.kpis.profit >= 0 ? 'emerald' : 'rose'} icon={BadgePercent} label={data.kpis.profit >= 0 ? 'Net Profit' : 'Net Loss'} value={fmtINR(Math.abs(data.kpis.profit))} sub={`Margin ${data.kpis.profit_margin_pct}%`} testid="kpi-profit" />
          </div>

          {/* Pies — Revenue + Expense */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="revenue-pie-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Revenue by {deptView === 'specialization' ? 'Specialization' : 'Department'}</CardTitle>
                <span className="text-[10px] text-slate-400">Click slice to drill down</span>
              </CardHeader>
              <CardContent>
                <PieBlock data={data.revenue.by_department} onSliceClick={(name) => drill('revenue', name)} />
              </CardContent>
            </Card>
            <Card data-testid="expense-pie-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Expense by Category</CardTitle>
                <span className="text-[10px] text-slate-400">Click slice to drill down</span>
              </CardHeader>
              <CardContent>
                <PieBlock data={data.expense.by_category} onSliceClick={(name) => drill('expense', name)} />
              </CardContent>
            </Card>
          </div>

          {/* v38.4 — Revenue by Payment Method + Patient Flow (Registrations vs Consultations) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="payment-method-pie-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><CreditCard size={14} /> Revenue by Payment Method</CardTitle>
                {paymentMethodFilter ? (
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-700" onClick={() => setPaymentMethodFilter(null)} data-testid="payment-method-clear">
                    <X size={12} className="mr-1" /> Clear: {paymentMethodFilter}
                  </Button>
                ) : (
                  <span className="text-[10px] text-slate-400">Click slice to filter invoices below</span>
                )}
              </CardHeader>
              <CardContent>
                <PieBlock
                  data={data.revenue.by_payment_method || []}
                  onSliceClick={(name) => setPaymentMethodFilter(prev => prev === name ? null : name)}
                  activeName={paymentMethodFilter}
                  testidPrefix="paymethod"
                />
              </CardContent>
            </Card>
            <Card data-testid="patient-flow-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Users size={14} /> Patient Flow · Registrations vs Consultations</CardTitle>
                <span className="text-[10px] text-slate-400">{data.range.label}</span>
              </CardHeader>
              <CardContent>
                {((data.patient_flow?.registrations || 0) + (data.patient_flow?.consultations || 0)) === 0 ? (
                  <div className="h-64 flex items-center justify-center text-xs text-slate-400">No patient activity in this range.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={[
                        { name: 'Registrations', value: data.patient_flow?.registrations || 0, fill: '#3b82f6' },
                        { name: 'Consultations', value: data.patient_flow?.consultations || 0, fill: '#10b981' },
                      ]}
                      margin={{ top: 10, right: 16, left: 4, bottom: 4 }}
                      barCategoryGap="30%"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip formatter={(v) => Number(v).toLocaleString('en-IN')} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {[
                          { name: 'Registrations', fill: '#3b82f6' },
                          { name: 'Consultations', fill: '#10b981' },
                        ].map((entry, idx) => (
                          <Cell key={idx} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="rounded-lg bg-blue-50 border border-blue-200 p-3" data-testid="pflow-registrations">
                    <p className="text-[10px] uppercase font-semibold text-blue-700">Registrations</p>
                    <p className="text-2xl font-bold text-blue-700 font-mono">{(data.patient_flow?.registrations || 0).toLocaleString('en-IN')}</p>
                    <p className="text-[10px] text-slate-500">New patients in window</p>
                  </div>
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3" data-testid="pflow-consultations">
                    <p className="text-[10px] uppercase font-semibold text-emerald-700">Consultations</p>
                    <p className="text-2xl font-bold text-emerald-700 font-mono">{(data.patient_flow?.consultations || 0).toLocaleString('en-IN')}</p>
                    <p className="text-[10px] text-slate-500">Appointments in window</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Invoice Details — v38.2 — clickable rows route to print view */}
          {data.revenue?.invoices?.length > 0 && (
            <Card data-testid="finance-invoice-details">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">
                  Invoice Details
                  <span className="text-xs font-normal text-slate-500 ml-2">
                    {(() => {
                      if (!paymentMethodFilter) return `${data.revenue.invoices.length} receipt(s) in this window`;
                      const matched = data.revenue.invoices.filter(inv => (inv.payment_method || 'Other') === paymentMethodFilter).length;
                      return `${matched} of ${data.revenue.invoices.length} receipt(s) · filtered by ${paymentMethodFilter}`;
                    })()}
                  </span>
                </CardTitle>
                {paymentMethodFilter && (
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-700" onClick={() => setPaymentMethodFilter(null)} data-testid="invoices-clear-method-filter">
                    <X size={12} className="mr-1" /> Clear filter
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Receipt #</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-left">Patient</th>
                        <th className="px-3 py-2 text-left">Service</th>
                        <th className="px-3 py-2 text-left">Department</th>
                        <th className="px-3 py-2 text-left">Payment Method</th>
                        <th className="px-3 py-2 text-left">Collected By</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                        <th className="px-3 py-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.revenue.invoices.map((inv, i) => {
                        const invMethod = inv.payment_method || 'Other';
                        const dimmed = paymentMethodFilter && invMethod !== paymentMethodFilter;
                        return (
                          <tr
                            key={inv.id || i}
                            className={`border-t border-slate-100 transition cursor-pointer ${dimmed ? 'opacity-30 hover:opacity-50' : 'hover:bg-emerald-50/50'}`}
                            onClick={() => window.open(`/invoice/print/${inv.id}`, '_blank')}
                            data-testid={`fin-invoice-row-${i}`}
                            data-payment-method={invMethod}
                            data-dimmed={dimmed ? '1' : '0'}
                          >
                            <td className="px-3 py-1.5 font-mono text-emerald-700 font-semibold">{inv.receipt_number || '—'}</td>
                            <td className="px-3 py-1.5 text-slate-500">{inv.created_at ? new Date(inv.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                            <td className="px-3 py-1.5">{inv.patient_name || '—'}</td>
                            <td className="px-3 py-1.5 text-slate-600">{inv.service}</td>
                            <td className="px-3 py-1.5"><span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold">{inv.department}</span></td>
                            <td className="px-3 py-1.5">
                              <span className="inline-block px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-semibold">{invMethod}</span>
                            </td>
                            <td className="px-3 py-1.5">
                              <div className="flex flex-col leading-tight">
                                <span className="font-medium text-slate-700">{inv.collected_by_name || '—'}</span>
                                {inv.collected_by_department && <span className="text-[10px] text-slate-400">{inv.collected_by_department}{inv.collected_by_role ? ` · ${inv.collected_by_role}` : ''}</span>}
                              </div>
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono font-bold text-slate-800">{fmtINR(inv.amount)}</td>
                            <td className="px-3 py-1.5 text-right">
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-600" onClick={(e) => { e.stopPropagation(); window.open(`/invoice/print/${inv.id}`, '_blank'); }} data-testid={`fin-invoice-print-${i}`}>
                                <Printer size={12} className="mr-1" /> Print
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Staff breakdown */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Staff Cost Breakdown by Role</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Role</th>
                      <th className="px-3 py-2 text-right">Headcount</th>
                      <th className="px-3 py-2 text-right">Monthly Salary</th>
                      <th className="px-3 py-2 text-right">Pro-rated ({data.range.days}d)</th>
                      <th className="px-3 py-2 text-right">Avg / Head</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expense.by_staff_category.map((row, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-medium">{row.name}</td>
                        <td className="px-3 py-1.5 text-right">{row.count}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmtINR(row.monthly_salary)}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmtINR(row.prorated)}</td>
                        <td className="px-3 py-1.5 text-right text-slate-500 font-mono">{fmtINR(row.count ? row.prorated / row.count : 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Trend chart */}
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Calendar size={14} /> Revenue & Profit Trend</CardTitle>
              <Select value={granularity} onValueChange={setGranularity}>
                <SelectTrigger className="w-32 h-8 text-xs" data-testid="fin-trend-gran"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hour">Hourly</SelectItem>
                  <SelectItem value="day">Daily</SelectItem>
                  <SelectItem value="week">Weekly</SelectItem>
                  <SelectItem value="month">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {trend?.series?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trend.series} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip formatter={(v) => fmtINR(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#revGrad)" name="Revenue" />
                    <Area type="monotone" dataKey="expense" stroke="#ef4444" strokeWidth={2} fill="url(#expGrad)" name="Expense" />
                    <Line type="monotone" dataKey="profit" stroke="#6366f1" strokeWidth={2} name="Profit" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-slate-400 py-8 text-center">No trend data for this range.</p>}
            </CardContent>
          </Card>
        </>
      )}

      {/* Drilldown side panel */}
      <Sheet open={drillOpen} onOpenChange={setDrillOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="finance-drilldown-panel">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              <span>{drillData?.side === 'revenue' ? 'Revenue' : 'Expense'} · {drillData?.bucket}</span>
              <Button size="sm" variant="ghost" onClick={() => setDrillOpen(false)}><X size={14} /></Button>
            </SheetTitle>
          </SheetHeader>
          {drillLoading ? (
            <div className="p-12 text-center text-slate-400 text-sm">Loading transactions…</div>
          ) : drillData?.rows?.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">No transactions in this bucket.</div>
          ) : drillData ? (
            <>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 my-3">
                <p className="text-[10px] uppercase font-semibold text-emerald-700">Subtotal · {drillData.count} entries</p>
                <p className="text-2xl font-bold text-emerald-700 font-mono">{fmtINR(drillData.total)}</p>
              </div>
              <Button size="sm" variant="outline" className="mb-3 w-full" onClick={() => seeAllForBucket(drillData.side, drillData.bucket)} data-testid="drill-see-all">
                See all in {drillData.side === 'revenue' ? 'Invoice History' : 'Expenses page'} <ArrowRight size={12} className="ml-1.5" />
              </Button>
              <div className="space-y-1.5">
                {drillData.rows.map((r) => (
                  <div key={r.id} className="text-xs rounded border border-slate-100 p-2 hover:bg-slate-50">
                    <div className="flex items-center justify-between">
                      <strong className="text-slate-800">{r.patient_name || r.vendor || '—'}</strong>
                      <span className="font-mono font-bold text-emerald-700">{fmtINR(r.amount)}</span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {r.service || r.description || '—'} {r.method ? `· ${r.method}` : ''} {r.payment_status ? `· ${r.payment_status}` : ''}
                    </p>
                    <p className="text-[9px] text-slate-400 mt-0.5">{new Date(r.created_at || r.date).toLocaleString('en-IN')} {r.receipt_number ? `· ${r.receipt_number}` : ''}</p>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const Kpi = ({ icon: Icon, color, label, value, sub, onClick, testid }) => {
  const colorMap = {
    emerald: 'bg-emerald-100 text-emerald-700', blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700', violet: 'bg-violet-100 text-violet-700',
    rose: 'bg-rose-100 text-rose-700', orange: 'bg-orange-100 text-orange-700',
  };
  return (
    <Card className={`${onClick ? 'cursor-pointer hover:shadow-md hover:border-emerald-300 transition' : ''}`} onClick={onClick} data-testid={testid}>
      <CardContent className="p-4">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${colorMap[color]}`}><Icon size={18} /></div>
        <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mt-2">{label}</p>
        <p className="text-xl font-bold text-slate-900 mt-0.5">{value}</p>
        {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
      </CardContent>
    </Card>
  );
};

const PieBlock = ({ data, onSliceClick, activeName, testidPrefix }) => {
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  if (!data?.length || total === 0) {
    return <div className="h-64 flex items-center justify-center text-xs text-slate-400">No data for this range.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" onClick={(slice) => onSliceClick(slice.name)} cursor="pointer">
            {data.map((d, idx) => {
              const dim = activeName && activeName !== d.name;
              return <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} fillOpacity={dim ? 0.25 : 1} />;
            })}
          </Pie>
          <Tooltip formatter={(v) => `₹ ${Number(v).toLocaleString('en-IN')}`} />
        </PieChart>
      </ResponsiveContainer>
      <div className="space-y-1 max-h-60 overflow-y-auto pr-1">
        {data.map((d, i) => {
          const pct = total ? Math.round((d.value / total) * 1000) / 10 : 0;
          const isActive = activeName === d.name;
          const dim = activeName && !isActive;
          return (
            <button
              key={i}
              onClick={() => onSliceClick(d.name)}
              className={`w-full text-left text-xs rounded p-2 border transition ${isActive ? 'bg-emerald-50 border-emerald-300' : 'border-transparent hover:bg-slate-50 hover:border-slate-200'} ${dim ? 'opacity-40' : ''}`}
              data-testid={`${testidPrefix || 'pie-row'}-${d.name}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <strong className="text-slate-700">{d.name}</strong>
                  <span className="text-slate-400 text-[10px]">{d.count ? `· ${d.count}` : ''}</span>
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold text-slate-800">{fmtINR(d.value)}</p>
                  <p className="text-[9px] text-slate-400">{pct}%</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FinanceDashboardPage;
